<template>
  <vf-media-gallery
    v-if="product"
    :items
    pagination
    :product-id
    @zoom="onZoom"
  >
    <template #default="{ i, item }">
      <div
        v-if="showsHotspots(item)"
        class="absolute-0 z-1"
        data-test-id="hotspot-outside-area"
        @click.stop="handleOutsideHotspotClick(item)"
      />
      <cms-shared-hot-zones
        v-if="hasHotspots(item) && !hotspotsHidden(item)"
        :media="{ sm: item.hotspotMedia }"
      />
      <base-button
        v-if="hasHotspots(item)"
        :aria-label="hotspotsHidden(item) ? $t.showFeatures : $t.hideFeatures"
        :aria-pressed="!hotspotsHidden(item)"
        class="group absolute bottom-0 right-0 z-3 mb-4 mr-6 items-center gap-x-2 rounded-sm bg-white px-3 py-2 overline-1 text-xs shadow-md i-flex"
        @click.stop="toggleHotspots(item)"
      >
        <vf-icon aria-hidden="true" :name="hotspotsHidden(item) ? 'visible' : 'hidden'" />
        <span
          aria-hidden="true"
          :class="{ 'hidden lg:group-hover:inline lg:group-focus-visible:inline': !labelVisible(item) }"
        >
          {{ hotspotsHidden(item) ? $t.showFeatures : $t.hideFeatures }}
        </span>
      </base-button>
      <base-button
        v-if="
          $feature.pdpShopTheLook
            && !!product.productSets?.length
            && (i === 0 || showShopTheLookButtonOnDesktop && i === 1)
            && showShopTheLook
        "
        class="absolute right-0 top-0 z-2 m-4 aspect-square f-col center rounded-full bg-white shadow-md"
        :class="[brandClasses.shopTheLookGalleryButton, {
          'lg:hidden': i === 0,
          '<lg:hidden': showShopTheLookButtonOnDesktop && i === 1,
        }]"
        style="min-height: 4.625rem"
        @click="scrollToElement('shopTheLook')"
      >
        <span
          class="mx-2 mt-4 aspect-square w-min f-col center"
          :class="brandClasses.shopTheLookButton"
        >
          <span class="text-center text-xs">
            {{ $t.shopTheLookHeading }}
          </span>

          <vf-icon class="c-black" dir="down" name="chevron" />
        </span>
      </base-button>

      <span
        v-if="showBadge && badgeLabel"
        class="absolute right-0 top-0 z-1 m-4 rounded-sm bg-brand-3 p-2 eyebrow"
        :class="{ hidden: i !== 0 }"
        style="letter-spacing: 0.031rem"
      >
        {{ badgeLabel }}
      </span>
    </template>
  </vf-media-gallery>
</template>

<script lang="ts" setup>
import type { ImageItem, MediaGalleryHotspotContent } from '#types/media'

const { brandClasses, gallery: { showShopTheLookButtonOnDesktop, showBadge } } = useAppConfig().pages?.pdp || {}
const { galleryItems, onZoom, product, productId, showShopTheLook } = usePdp()

const hiddenHotspotImageSources = ref(new Set<string>())
const labels = ref(new Map<string, { visible: boolean, lastToggleAt: number }>())
const outsideClicks = new Map<string, number>()
const DOUBLE_TAP_MS = 350

const hasHotspots = (item: ImageItem): item is ImageItem & { hotspotMedia: MediaGalleryHotspotContent } =>
  Boolean(item.hotspotMedia?.enableImageMap && item.hotspotMedia.imageMap?.length)

const hotspotsHidden = (item: ImageItem) => hiddenHotspotImageSources.value.has(item.src)
const showsHotspots = (item: ImageItem) => hasHotspots(item) && !hotspotsHidden(item)
const labelVisible = (item: ImageItem) => labels.value.get(item.src)?.visible ?? true

// Galleries render a plain picture without srcZoom while hotspots are visible.
const items = computed(() => galleryItems.value.map((item) =>
  item.type === 'image' && showsHotspots(item) ? { ...item, srcZoom: undefined } : item))

const toggleHotspots = (item: ImageItem) => {
  const previousLabel = labels.value.get(item.src)
  const now = Date.now()
  const visible = previousLabel?.visible === false && now - previousLabel.lastToggleAt <= DOUBLE_TAP_MS
  labels.value.set(item.src, { visible, lastToggleAt: visible ? 0 : now })

  if (hotspotsHidden(item)) hiddenHotspotImageSources.value.delete(item.src)
  else hiddenHotspotImageSources.value.add(item.src)
  outsideClicks.delete(item.src)
}

const handleOutsideHotspotClick = (item: ImageItem) => {
  const count = (outsideClicks.get(item.src) ?? 0) + 1
  if (count < 2) {
    outsideClicks.set(item.src, count)
    return
  }

  outsideClicks.delete(item.src)
  hiddenHotspotImageSources.value.add(item.src)
}

const badgeLabel = computed(() => {
  const badge = product.value?.badge
  return Array.isArray(badge) ? badge[0]?.label : badge?.label
})
</script>
